import { Suspense, useEffect, useState } from 'react';
import { Navigate, useLocation, useMatch } from 'react-router-dom';
import { couldBeCareersSlug } from '../../lib/careers/careersPage';
import { resolveCareersSlug, type SlugResolution } from '../../lib/careers/careersRepo';
import EmptyState from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/button';
import { landingReferrerFor, rememberJobSource } from '../../lib/applications/jobSourceCapture';
import { useAuth } from '../../context/AuthContext';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import Skeleton from '../../components/ui/skeleton';

const PublicJobPage = lazyWithRetry(() => import('./PublicJobPage'));
// Typed through ComponentType<unknown> for lazyWithRetry; the real props are passed below.
const CompanyCareersPage = lazyWithRetry(() => import('./CompanyCareersPage') as unknown as Promise<{ default: React.ComponentType<unknown> }>) as unknown as React.ComponentType<{ orgId: string; org: Record<string, unknown>; slug: string }>;

/**
 * One URL per job. /jobs/:jobId and its Apply destination /jobs/:jobId/apply both sit inside the
 * candidate (EMT) layout, which sends a signed-out visitor to the landing page — so a link an
 * employer pasted into their own website used to be a dead end for everyone without an account,
 * and clicking Apply landed a stranger on the marketing home page, which never mentions the job.
 *
 * This gate wraps that layout: on either of a job's public URLs, anyone who is NOT a signed-in
 * candidate gets the public posting instead; candidates fall through to the in-app pages exactly as
 * before (match score, their application status, the real application form, no layout remount).
 *
 * On /jobs/:jobId/apply the posting renders its "create your account to apply" card and remembers
 * this exact URL, so finishing signup returns the visitor to the application they came for
 * (savePostLoginRedirect → EMTRegStep1.finishStep1 / redirectByRole). Every other candidate route
 * is untouched.
 *
 * Company pages (2026-11-05): /jobs/<slug> is a company's own careers page when <slug> is a page name
 * a company claimed (careerSlugs/<slug>, src/lib/careers). Only slug-shaped values are looked up
 * (a job's auto ID is mixed case and 20 characters, which a slug can never be), so job links cost no
 * extra read. Slugs can never equal a job ID (the server refuses), so the two never collide. An old
 * page name redirects to the company's current one; /jobs/<slug>/apply goes to the page itself.
 */
export default function PublicJobGate({ children }: { children: React.ReactNode }) {
  const listingMatch = useMatch('/jobs/:jobId');
  const applyMatch = useMatch('/jobs/:jobId/apply');
  const { user, role, profile, loading, profileReady } = useAuth();
  const jobId = applyMatch?.params.jobId ?? listingMatch?.params.jobId;
  const slugShaped = !!jobId && jobId !== 'companies' && couldBeCareersSlug(jobId);
  const [resolved, setResolved] = useState<{ x: string; r: SlugResolution | null; failed: boolean; attempt: number } | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    if (!slugShaped || !jobId) return;
    let active = true;
    resolveCareersSlug(jobId)
      .then((r) => active && setResolved({ x: jobId, r, failed: false, attempt: retryKey }))
      .catch((err) => {
        console.error('[PublicJobGate] page name lookup failed', err);
        if (active) setResolved({ x: jobId, r: null, failed: true, attempt: retryKey });
      });
    return () => {
      active = false;
    };
  }, [slugShaped, jobId, retryKey]);
  // Where this visit to the job came from (?src= tag, a Google referrer, else a direct link), kept in
  // this tab so the application records it after sign-up (src/lib/applications/jobSourceCapture.ts).
  const { search } = useLocation();
  useEffect(() => {
    if (!jobId || jobId === 'companies') return;
    rememberJobSource(jobId, search, landingReferrerFor(jobId));
  }, [jobId, search]);
  if (slugShaped && jobId) {
    const current = resolved && resolved.x === jobId && resolved.attempt === retryKey ? resolved : null;
    if (!current) {
      return (
        <div className="p-6" data-testid="jobs-slug-resolving">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full mt-4" />
        </div>
      );
    }
    if (current.failed) {
      return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 min-w-0" data-testid="public-job-error">
          <EmptyState title="We couldn't load this page" description="Please check your connection and try again." />
          <div className="mt-4 flex justify-center">
            <Button variant="outline" className="min-h-11" onClick={() => setRetryKey((k) => k + 1)}>
              Try again
            </Button>
          </div>
        </div>
      );
    }
    const r = current.r;
    if (r?.kind === 'redirect') return <Navigate to={`/jobs/${r.to}${search}`} replace />;
    if (r?.kind === 'company') {
      if (applyMatch) return <Navigate to={`/jobs/${r.slug}${search}`} replace />;
      return (
        <Suspense fallback={<div className="p-6"><Skeleton className="h-40 w-full" /></div>}>
          <CompanyCareersPage orgId={r.orgId} org={r.org} slug={r.slug} />
        </Suspense>
      );
    }
    // Not a company page name: a job link (for example seed-job-...), handled below as before.
  }
  // "/jobs/companies" is the employer map, a candidate screen, not a job id.
  if (!jobId || jobId === 'companies' || loading) return <>{children}</>;

  const effectiveRole = (profile?.role as string | undefined) ?? role;
  // Signed in, profile loaded, and still no role (never picked EMT or employer): the public posting,
  // whose header offers "Finish sign-up", not the candidate layout (QA 2026-10-06). While the profile is
  // still loading, the existing guard decides.
  const signedInWithoutRole = !!user && profileReady && !effectiveRole;
  const showPublic = !user || signedInWithoutRole || (!!effectiveRole && effectiveRole !== 'emt');
  if (!showPublic) return <>{children}</>;

  return (
    <Suspense
      fallback={
        <div className="p-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full mt-4" />
        </div>
      }
    >
      <PublicJobPage />
    </Suspense>
  );
}
